import axios from "axios";
import { DisplayColorEnum, OpenAPI } from "@propertelligent/client-api";

// TODO(client-api-regen): Replace this thin axios shim with the generated
// `@propertelligent/client-api` OrganizationUser WhatsApp-sender service/types after
// the backend PR merges and CI republishes the SDK.

export enum WhatsAppSenderStatusEnum {
  None = 0,
  Pending = 1,
  Active = 2,
  Failed = 3,
  Offline = 4,
}

export interface WhatsAppSenderStatusVM {
  phoneNumber: string;
  senderStatus: WhatsAppSenderStatusEnum;
  updatedAtUtc: string | null;
}

export interface WhatsAppSenderVerifyVM {
  code: string;
}

export interface SingleItemResponse<T> {
  success?: T | null;
  errors?: Record<string, string[]>;
}

/** Additive field on OrganizationUserVM until client-api republishes. */
export type OrganizationUserWhatsAppSenderFields = {
  whatsAppSenderStatus?: WhatsAppSenderStatusEnum;
};

const STATUS_TAG_COLOR_BY_DISPLAY: Record<DisplayColorEnum, string> = {
  [DisplayColorEnum.Default]: "gray",
  [DisplayColorEnum.Success]: "green",
  [DisplayColorEnum.Info]: "blue",
  [DisplayColorEnum.Warning]: "yellow",
  [DisplayColorEnum.Danger]: "red",
};

export const getWhatsAppSenderStatusDisplay = (
  status?: WhatsAppSenderStatusEnum | null,
): { label: string; color: string } => {
  let colorEnum = DisplayColorEnum.Default;
  let label = "Not connected";

  if (status === WhatsAppSenderStatusEnum.Pending) {
    colorEnum = DisplayColorEnum.Warning;
    label = "Pending verification";
  } else if (status === WhatsAppSenderStatusEnum.Active) {
    colorEnum = DisplayColorEnum.Success;
    label = "Active";
  } else if (status === WhatsAppSenderStatusEnum.Failed) {
    colorEnum = DisplayColorEnum.Danger;
    label = "Failed";
  } else if (status === WhatsAppSenderStatusEnum.Offline) {
    colorEnum = DisplayColorEnum.Default;
    label = "Offline";
  }

  return {
    label,
    color: STATUS_TAG_COLOR_BY_DISPLAY[colorEnum],
  };
};

const baseUrl = (): string =>
  typeof OpenAPI.BASE === "string" ? OpenAPI.BASE : "";

const authHeaders = (): Record<string, string> => {
  const token =
    typeof OpenAPI.TOKEN === "string" ? OpenAPI.TOKEN : undefined;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

type AxiosLikeError = {
  message?: string;
  response?: { status?: number; data?: unknown };
};

// The generated `@propertelligent/client-api` throws `ApiError` (with `status` + `body`), and that is
// the shape `apiHelper` reads to surface backend field errors. Raw axios throws `AxiosError` instead,
// so without this normalization every server validation error from these endpoints is swallowed.
const toApiError = (error: unknown): unknown => {
  const axiosError = error as AxiosLikeError;

  if (!axiosError?.response) {
    return error;
  }

  const normalized = new Error(
    axiosError.message || "Request failed",
  ) as Error & { status?: number; body?: unknown };

  normalized.status = axiosError.response.status;
  normalized.body = axiosError.response.data;

  return normalized;
};

const request = async <T>(
  operation: () => Promise<{ data: T }>,
): Promise<T> => {
  try {
    const response = await operation();
    return response.data;
  } catch (error) {
    throw toApiError(error);
  }
};

export const getMyWhatsAppSender = async (): Promise<
  SingleItemResponse<WhatsAppSenderStatusVM>
> => {
  return request(() => axios.get(
    `${baseUrl()}/api/OrganizationUser/me/whatsapp-sender`,
    { headers: authHeaders() },
  ));
};

export const registerMyWhatsAppSender = async (): Promise<
  SingleItemResponse<WhatsAppSenderStatusVM>
> => {
  return request(() => axios.post(
    `${baseUrl()}/api/OrganizationUser/me/whatsapp-sender/register`,
    {},
    { headers: authHeaders() },
  ));
};

export const verifyMyWhatsAppSender = async (
  code: string,
): Promise<SingleItemResponse<WhatsAppSenderStatusVM>> => {
  const payload: WhatsAppSenderVerifyVM = { code };
  return request(() => axios.post(
    `${baseUrl()}/api/OrganizationUser/me/whatsapp-sender/verify`,
    payload,
    { headers: authHeaders() },
  ));
};
